import luhn from 'fast-luhn';
import {
  checkVAT,
  Country,
  finland as jsVatFinland,
  germany as jsVatGermany,
  norway as jsVatNorway,
  sweden as jsVatSweden,
} from 'jsvat-next';

import { Company } from '../../react/components/register-company/state/form-state-provider';
import { assertNever } from '../variables';

/**
 * Regular expression for validating email address. The email address must be 7-bit ASCII in order
 * to be sent by AWS SES. This regex represents an even stricter set of characters that probably
 * is enough.
 *
 * Make sure this regex matches any regex in backend.
 */
export const emailRegex =
  /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[_a-z0-9-]+(\.[_a-z0-9-]+)*)?@[a-z0-9-]+(\.[a-z0-9-]+)*(\.[a-z]{2,4})$/;

/**
 * Returns a jsvat package validation object for the specifiec country code.
 */
const getJsVatCountryByCountryCode = (countryCode: Company['countryCode']): Country => {
  switch (countryCode) {
    case 'DE':
      return jsVatGermany;
    case 'FI':
      return jsVatFinland;
    case 'NO':
      return jsVatNorway;
    case 'SE':
      return jsVatSweden;
    default:
      assertNever(countryCode);
  }
};

/**
 * Checks whether value is a valid Swedish organisation number.
 * @param orgNumber Can be personal identification number if organization is a sole proprietorship.
 */
export function isValidSwedishOrganisationNumber(orgNumber: string) {
  if (/^\d{6}-\d{4}$/.test(orgNumber) === false) {
    return false;
  }

  // TODO: Check third number if organisation number is non-sole proprietorship.
  // https://www.fortnox.se/fortnox-foretagsguide/ekonomisk-ordlista/organisationsnummer/
  return luhn(orgNumber.replace('-', ''));
}

/**
 * Returns an object with information specifying whether the current vat number is valid for the
 * specified country. It also returns a cleaned vat number (spaces removed).
 */
export const checkVat = (countryCode: Company['countryCode'], vatNumber: string) => {
  const result = checkVAT(vatNumber, [getJsVatCountryByCountryCode(countryCode)]);
  const valueWhitespacesRemoved = vatNumber.replaceAll(' ', '');

  // If vat is valid, saved cleaned vat.
  return result.isValid && result.value === valueWhitespacesRemoved
    ? { isValid: true, cleanedVat: result.value }
    : { isValid: false, cleanedVat: undefined };
};

export const isValidUuidV4 = (value?: string) => {
  if (!value) {
    return false;
  }

  return /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(value);
};
